/**
 * Implicit roles: grants a person holds because of where they sit in the org chart, not because
 * anyone granted them by hand (ADR 0024).
 *
 *   - Anyone who currently manages at least one active employment is a `manager` at
 *     `reporting_tree` scope.
 *   - The head of a department is a `department_head` at `department` scope for that department.
 *   - A member of a helpdesk team is a `helpdesk_agent` at `org` scope (ADR 0028). The scope is
 *     not what limits them: the API lets an agent work only their teams' categories.
 *   - A member of a travel desk team is a `travel_desk_agent` ("Travel Desk Admin") at `org` scope
 *     (ADR 0030). Likewise the API, not the scope, limits them to their teams' coverage.
 *
 * They are DERIVED when a session is authenticated and never stored as `role_grants` rows. The
 * position is the source of truth: the moment someone gets their first report they are a
 * Reporting Manager, and the moment the last one leaves they are not. What those roles may DO is
 * still the organisation's own permission set for `manager` / `department_head`, which it edits on
 * the Roles & Permissions page like any other role.
 *
 * Pure on purpose, like can.ts: the API resolves the org-chart facts, this decides what they mean.
 */

import type { Grant } from './can.js';
import type { Role, Scope } from './roles.js';

/**
 * Which roles the org chart grants, at what scope, and whether a person may ALSO be given the role
 * by hand. `manager` may: a covering manager during a leave of absence, or a manager-to-be with no
 * reports yet, keeps a hand grant. `department_head` may not: headship is set on the department
 * itself, and a hand grant would be a second, drifting source of the same fact.
 */
export const IMPLICIT_ROLES: Readonly<
  Partial<Record<Role, { readonly scope: Scope; readonly handAssignable: boolean }>>
> = Object.freeze({
  manager: { scope: 'reporting_tree', handAssignable: true },
  department_head: { scope: 'department', handAssignable: false },
  // Membership is set on the helpdesk team; a hand grant would be a second, drifting source.
  helpdesk_agent: { scope: 'org', handAssignable: false },
  // Same reasoning: membership is set on the travel desk team (ADR 0030).
  travel_desk_agent: { scope: 'org', handAssignable: false },
});

/** Does the org chart grant this role to anyone? */
export function isImplicitRole(role: string): boolean {
  return role in IMPLICIT_ROLES;
}

/** May an administrator grant this role by hand on the Roles & Permissions page? */
export function isHandAssignable(role: string): boolean {
  return IMPLICIT_ROLES[role as Role]?.handAssignable ?? true;
}

/** The org-chart facts about one person, as the API resolves them. */
export interface OrgChartPosition {
  /** True when at least one active employment currently reports to one of theirs. */
  readonly managesSomeone: boolean;
  /** The active departments whose head is one of their active employments. */
  readonly headedDepartmentIds: readonly string[];
  /** True when they belong to at least one active helpdesk team (ADR 0028). */
  readonly isHelpdeskAgent?: boolean;
  /**
   * True when they belong to at least one active travel desk team (ADR 0030). Absent = false.
   * `apps/api/src/lib/implicit-grants.ts` does not set it yet: the desk team tables arrive with
   * TD-2.1 and the loader wiring lands with them, so until then nobody derives `travel_desk_agent`.
   */
  readonly isTravelDeskAgent?: boolean;
}

/**
 * The grants a position carries. Order is stable (manager, departments sorted, helpdesk agent,
 * travel desk agent).
 */
export function deriveImplicitGrants(position: OrgChartPosition): Grant[] {
  const grants: Grant[] = [];
  if (position.managesSomeone) {
    grants.push({ role: 'manager', scope: 'reporting_tree', scopeId: null });
  }
  for (const departmentId of [...new Set(position.headedDepartmentIds)].sort()) {
    grants.push({ role: 'department_head', scope: 'department', scopeId: departmentId });
  }
  if (position.isHelpdeskAgent) {
    grants.push({ role: 'helpdesk_agent', scope: 'org', scopeId: null });
  }
  if (position.isTravelDeskAgent) {
    grants.push({ role: 'travel_desk_agent', scope: 'org', scopeId: null });
  }
  return grants;
}

const grantKey = (g: Grant) => `${g.role}|${g.scope}|${g.scopeId ?? ''}`;

/**
 * Stored grants plus implicit ones. A union, never a replacement: a hand-granted manager with no
 * reports keeps their grant, and an implicit grant identical to a stored one appears once.
 */
export function mergeGrants(stored: readonly Grant[], implicit: readonly Grant[]): Grant[] {
  const seen = new Set(stored.map(grantKey));
  const merged = [...stored];
  for (const grant of implicit) {
    if (seen.has(grantKey(grant))) continue;
    seen.add(grantKey(grant));
    merged.push(grant);
  }
  return merged;
}
